import lodash, { find, isEmpty, isEqual } from 'lodash'
import moment from 'moment-business-days'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { Variant } from 'react-bootstrap/types'

import Alert from '../../../../../../1stlog-ui/src/components/Alert'
import { useField } from '../../../../../../1stlog-ui/src/components/Field'
import useFieldState from '../../../../../../1stlog-ui/src/components/Field/useFieldState'
import useFieldValue from '../../../../../../1stlog-ui/src/components/Field/useFieldValue'
import { usePrevious } from '../../../../../../1stlog-ui/src/hooks'
import { toast } from '../../../../../../1stlog-ui/src/utils'
import { useProducts, useSelectedProduct } from '../../../Products'
import useOrderEntry from '../../../useOrderEntry'

const ProductHandler = (): ReactElement => {
  const [alert, setAlert] = useState<{
    label: string
    variant: Variant
  } | null>(null)

  const clearAlert = (): void => setAlert(null)

  const [isInit, setIsInit] = useState(true)

  // TODO: to only use initialValues and not last selected sla, just use initialValues.defaultSlaTypeId
  const [defaultSlaTypeId, setDefaultSlaTypeId] = useFieldState(
    'defaultSlaTypeId',
    {
      ignorePrefix: true,
    },
  )
  // TODO: to only use initialValues and not last selected sla, just use initialValues.defaultSlaTypeName
  const [defaultSlaTypeName, setDefaultSlaTypeName] = useFieldState(
    'defaultSlaTypeName',
    {
      ignorePrefix: true,
    },
  )

  const { initialValues } = useOrderEntry()

  const { products } = useProducts()

  const selectedProduct = useSelectedProduct()

  const [selectedProductId, setSelectedProductId] = useField(
    'selectedProductId',
    { asState: true },
  )

  const [productSettings, setProductSettings] = useField('productSettings', {
    asState: true,
  })

  const [date, setDate] = useField('date', { asState: true })

  const [desiredProductId, setDesiredProductId] = useState<number | null>(null)

  // Select product when desired product id canges
  const previousDesiredProductId = usePrevious(desiredProductId)
  useEffect(() => {
    if (previousDesiredProductId !== desiredProductId) {
      // console.log('Product selection desired', {
      //   previousDesiredProductId,
      //   desiredProductId,
      // })
      // Only select if not disabled
      const product = find(products, (p) => p.id === desiredProductId)
      if (product && !product.disabled) {
        // console.log('Selecting product', product.id)
        setSelectedProductId(product.id)
      } else {
        // console.log('Cannot select product', { id: desiredProductId, product })
        setKeepAlert(true)
        setSelectedProductId(null)
      }
    }
  }, [desiredProductId])

  const [keepAlert, setKeepAlert] = useState(false)
  const previousDate = usePrevious(date)
  const previousSelectedProductId = usePrevious(selectedProductId)
  useEffect(() => {
    if (
      previousDate !== date ||
      previousSelectedProductId !== selectedProductId
    ) {
      // clear alert
      if (keepAlert) {
        setKeepAlert(false)
        // console.log('Should clean alert, but just ressettting keep state')
      } else {
        // console.log('Clearing alert...', alert)
        clearAlert()
      }
    }
  }, [date, selectedProductId])

  // Save new default sla
  const previousSelectedProduct = usePrevious(selectedProduct)
  useEffect(() => {
    if (previousSelectedProduct !== selectedProduct && selectedProduct) {
      setDefaultSlaTypeId(selectedProduct.slaTypeId)
      setDefaultSlaTypeName(selectedProduct.slaType)
    }
  }, [selectedProduct])

  // product list or product changed
  const previousProducts = usePrevious(products)
  // const previousSelectedProduct = usePrevious(selectedProduct)
  useEffect(() => {
    if (!products || (isEmpty(previousProducts) && isEmpty(products))) {
      return
    }

    // console.log('PRODUCT CHECK LOOP', {
    //   products,
    //   previousProducts,
    //   isInit,
    //   defaultSlaTypeId: initialValues.defaultSlaTypeId,
    //   defaultSlaTypeName: initialValues.defaultSlaTypeName,
    //   selectedProductId,
    // })

    // If defaultSlaTypeId === -1, then check with defaultSlaTypeName

    // Initial selection (default sla)
    const searchBySlaTypeName = defaultSlaTypeId === -1

    const defaultProduct = find(products, (p) =>
      searchBySlaTypeName
        ? p.slaType === defaultSlaTypeName
        : p.slaTypeId === defaultSlaTypeId,
    )

    if (isInit && defaultSlaTypeId) {
      // console.log('do initial selection')

      setIsInit(false)

      if (defaultProduct) {
        const isToday = lodash.isEqual(
          moment().format('DD.MM.YYYY'),
          moment(date).format('DD.MM.YYYY'),
        )
        const nextBusinessDay = moment()
          .startOf('day')
          .nextBusinessDay()
          .toISOString()
        // const nextBusinessDayIsToday = isEqual(
        //   moment(nextBusinessDay).format('DD.MM.YYYY'),
        //   moment(date).format('DD.MM.YYYY'),
        // )
        if (defaultProduct.disabled) {
          // Check on next day

          if (isToday) {
            setAlert({
              variant: 'info',
              label: i18n.t(
                'product_handler_desired_product_disabled_next_workday',
                {
                  defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) ist heute nicht mehr verfügbar, es wurde nun automatisch zum nächsten Werktag gewechselt`,
                  slaName: initialValues.defaultSlaTypeName,
                },
              ),
            })
            setKeepAlert(true)
            // setTimeout(() => setDate(nextBusinessDay), 0)
            setDate(nextBusinessDay)
          } else {
            setAlert({
              variant: 'warning',
              label: i18n.t('product_handler_desired_product_not_available', {
                defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) ist nicht mehr verfügbar. Bitte prüfen Sie das gewählte Datum.`,
                slaName: initialValues.defaultSlaTypeName,
              }),
            })
          }
          setDesiredProductId(null)
        } else {
          setAlert({
            variant: 'success',
            label: isToday
              ? i18n.t(
                  'product_handler_desired_product_automatically_selected',
                  {
                    defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) wurde bereits für Sie ausgewählt.`,
                    slaName: initialValues.defaultSlaTypeName,
                  },
                )
              : i18n.t(
                  'product_handler_desired_product_not_available_today_automatically_selected_next_workday',
                  {
                    defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) ist heute nicht mehr verfügbar. Es wurde daher automatisch am nächsten Werktag für Sie ausgewählt.`,
                    slaName: initialValues.defaultSlaTypeName,
                  },
                ),
          })
          setKeepAlert(true)
          // Select this product
          // console.log('Selecting initial product...')
          setDesiredProductId(Number(defaultProduct.id))
        }
      } else {
        setIsInit(false)
        setAlert({
          variant: 'warning',
          label: i18n.t(
            'product_handler_desired_product_not_found_check_date',
            {
              defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) wurde nicht gefunden. Bitte prüfen Sie das gewählte Datum.`,
              slaName: initialValues.defaultSlaTypeName,
            },
          ),
        })
        setDesiredProductId(null)
      }
    }

    // Keep previous selection
    if ((!isInit || !initialValues.defaultSlaTypeId) && selectedProductId) {
      setIsInit(false)
      const product = find(products, (p) => p.id === selectedProductId)
      // console.log('!isInit', { isInit, selectedProductId, products, product })
      if (product) {
        if (product.disabled) {
          setAlert({
            variant: 'warning',
            label: i18n.t(
              'product_handler_selected_product_not_available_check_date',
              {
                defaultValue: `Ihr zuvor gewähltes Produkt ({{slaName}}) ist heute nicht mehr verfügbar. Bitte prüfen Sie das gewählte Datum.`,
                slaName: product.slaType,
              },
            ),
          })
          setDesiredProductId(null)
        } else {
          // console.log('Selecting previous product...')
          setDesiredProductId(Number(product.id))
        }
      } else {
        setAlert({
          variant: 'warning',
          label: i18n.t(
            'product_handler_selected_product_not_found_check_date',
            {
              defaultValue: `Ihr zuvor gewähltes Produktwurde nicht gefunden. Bitte prüfen Sie das gewählte Datum.`,
            },
          ),
        })
        setDesiredProductId(null)
      }
    }

    if (!isInit && !selectedProductId && initialValues.defaultSlaTypeId) {
      if (defaultProduct) {
        if (!defaultProduct.disabled) {
          setKeepAlert(true)
          setAlert({
            variant: 'success',
            label: i18n.t(
              'product_handler_desired_product_automatically_selected',
              {
                defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) wurde bereits für Sie ausgewählt.`,
                slaName: defaultProduct.slaType,
              },
            ),
          })
          setDesiredProductId(Number(defaultProduct.id))
        }
      } else {
        setAlert({
          variant: 'warning',
          label: i18n.t(
            'product_handler_desired_product_not_found_check_date',
            {
              defaultValue: `Ihr gewünschtes Produkt ({{slaName}}) wurde nicht gefunden. Bitte prüfen Sie das gewählte Datum.`,
              slaName: initialValues.defaultSlaTypeName,
            },
          ),
        })
      }
    }
  }, [selectedProduct, products])

  // product was changed
  useEffect(() => {
    // TODO: check current product settings and reset if needed
    // FIXME: for now just reset all settings
    setTimeout(() => setProductSettings(undefined), 0)
  }, [selectedProduct])

  if (alert) {
    return (
      <div className="mx-2 mt-2">
        <Alert variant={alert.variant} dismissible onClose={clearAlert}>
          {alert.label}
        </Alert>
      </div>
    )
  }

  return <></>
}

export default ProductHandler
